import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PaginationProps } from '../../types';

export const Pagination: React.FC<PaginationProps> = React.memo(
  ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    onNextPage,
    onPreviousPage,
  }) => {
    // Don't render pagination if there's only one page or no items
    if (totalPages <= 1 || totalItems === 0) {
      return null;
    }

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    };

    return (
      <div
        className="flex items-center justify-between px-4 py-3 border-t transition-colors duration-200"
        style={{
          borderColor: 'var(--color-border-default)',
          backgroundColor: 'var(--color-bg-secondary)',
        }}
      >
        {/* Results info */}
        <div className="flex items-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <span>
            Showing {startItem} to {endItem} of {totalItems} repositories
          </span>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center space-x-2">
          {/* Page info */}
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Page {currentPage} of {totalPages}
          </span>

          {/* Previous button */}
          <button
            onClick={onPreviousPage}
            disabled={!hasPreviousPage}
            onKeyDown={(e) => handleKeyDown(e, onPreviousPage)}
            className={`
            flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200
            ${hasPreviousPage ? 'hover:shadow-xs cursor-pointer' : 'opacity-50 cursor-not-allowed'}
          `}
            aria-label="Go to previous page"
            aria-disabled={!hasPreviousPage}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </button>

          {/* Next button */}
          <button
            onClick={onNextPage}
            disabled={!hasNextPage}
            onKeyDown={(e) => handleKeyDown(e, onNextPage)}
            className={`
            flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200
            ${hasNextPage ? 'hover:shadow-xs cursor-pointer' : 'opacity-50 cursor-not-allowed'}
          `}
            aria-label="Go to next page"
            aria-disabled={!hasNextPage}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';
